import { z } from 'zod'
import { http } from './http'
import { auth } from './auth'
import { Service } from '../types'
import { parseWithZod } from '../utils/parse-with-zod'

type OauthAccessParams = {
  clientId: string
  customerId: number
  authNonce: string
  dmsId: number
}

const OAuthUrlResponseSchema = z.object({
  url: z.string(),
})

const OAuthAccessSchema = z.object({
  has_access: z.boolean(),
})

async function getOauthAccess({
  clientId,
  customerId,
  authNonce,
  dmsId,
}: OauthAccessParams): Promise<
  | {
      hasAccess: boolean
      error?: never
    }
  | {
      error: 'popup-blocked' | 'popup-closed'
      hasAccess?: never
    }
> {
  const { code, state, error } = await getAuthCode({
    clientId,
    customerId,
    authNonce,
  })

  if (error) {
    return {
      error,
    }
  }

  if (!code || !state) {
    throw new Error('Invalid OAuth response: code or state is missing')
  }

  await exchangeDMSCode({
    code,
    stateToken: state,
    dmsId,
  })
  return {
    hasAccess: true,
  }
}

type AuthCodeResponse = {
  code?: string | null
  state?: string | null
  error?: 'popup-blocked' | 'popup-closed'
}

let promise: Promise<AuthCodeResponse> | null = null

async function getAuthCode({
  clientId,
  customerId,
  authNonce,
}: Omit<OauthAccessParams, 'dmsId'>): Promise<AuthCodeResponse> {
  const oauthUrl = await generateNonceAuthorizeUrl({
    authNonce,
    clientId,
    customerId,
  })

  if (promise) {
    return promise
  }
  promise = new Promise<AuthCodeResponse>(resolve => {
    const popup = window.open(oauthUrl, 'oauth')
    if (!popup) {
      resolve({
        error: 'popup-blocked',
      })
      return
    }
    const interval = setInterval(() => {
      if (popup.closed) {
        clearInterval(interval)
        resolve({
          error: 'popup-closed',
        })
        return
      }
      let oauthUrl: URL | null = null
      try {
        oauthUrl = new URL(popup.location.href)
      } catch (e) {
        /**
         * ignore security error
         * ...
         * TODO:
         * instead of litening to the url, which throws a SecurityError (CORS error)
         * while is in the SSO provider domain, we should use window.postMessage
         * after the oauth flow is done
         */
        console.error('Security error', e)
      }
      const code = oauthUrl?.searchParams.get('code')
      const state = oauthUrl?.searchParams.get('state')
      if (code) {
        popup.close()
        clearInterval(interval)
        resolve({
          code,
          state,
        })
        promise = null
      }
    }, 1000)
  })
  return promise
}

const CheckClientAccessSchema = z.object({
  has_access: z.boolean(),
  dms_id: z.number(),
})

async function checkClientAccess(clientId: string, customerId: number) {
  const userRegionHost = auth.getUserRegionHost()
  const response = await http.post(`${userRegionHost}/api/oauth/check_client_access`, {
    client_id: clientId,
    customer_id: customerId,
  })

  return parseWithZod(CheckClientAccessSchema, response)
}

const NonceAuthorizeUrlSchema = z.object({
  url: z.string(),
})

async function generateNonceAuthorizeUrl({
  authNonce,
  clientId,
  customerId,
}: {
  authNonce: string
  clientId: string
  customerId: number
}) {
  const userRegionHost = auth.getUserRegionHost()
  const response = await http.post(`${userRegionHost}/api/oauth/generate_nonce_authorize_url`, {
    auth_nonce: authNonce,
    client_id: clientId,
    customer_id: customerId,
  })
  return parseWithZod(NonceAuthorizeUrlSchema, response).url
}

async function generateOAuthUrl({
  service,
  userId,
  regionHost,
}: {
  service: Service
  userId: number
  regionHost?: string
}) {
  const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
  const response = await http.post(`${host}/api/oauth/generate_authorize_url`, {
    service,
    user_id: userId,
  })
  return parseWithZod(OAuthUrlResponseSchema, response)
}

async function generateDMSOAuthUrl({
  userId,
  dmsId,
  regionHost,
}: {
  userId: number
  dmsId: number
  regionHost?: string
}) {
  const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
  const response = await http.post(`${host}/api/oauth/generate_dms_authorize_url`, {
    user_id: userId,
    dms_id: dmsId,
  })
  return parseWithZod(OAuthUrlResponseSchema, response)
}

async function exchangeOAuthCode({
  service,
  userId,
  code,
  stateToken,
  regionHost,
}: {
  service: Service
  userId: number
  code: string
  stateToken: string
  regionHost?: string
}) {
  const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
  return http.post(`${host}/api/oauth/exchange_code`, {
    service,
    user_id: userId,
    code,
    state_token: stateToken,
  })
}

async function exchangeDMSCode({
  code,
  stateToken,
  dmsId,
  regionHost,
}: {
  code: string
  stateToken: string
  dmsId: number
  regionHost?: string
}) {
  const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
  return http.post(`${host}/api/oauth/exchange_dms_code`, {
    code,
    state_token: stateToken,
    dms_id: dmsId,
  })
}

async function checkOAuthAccess({
  service,
  userId,
  regionHost,
}: {
  service: Service
  userId: number
  regionHost?: string
}) {
  const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
  const response = await http.post(`${host}/api/oauth/check_access`, {
    service,
    user_id: userId,
  })
  return parseWithZod(OAuthAccessSchema, response)
}

async function disconnectOAuthProvider({
  service,
  userId,
  regionHost,
}: {
  service: Service
  userId: number
  regionHost?: string
}) {
  const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
  return await http.post(`${host}/api/oauth/disconnect_provider`, {
    service,
    user_id: userId,
  })
}

// Per-DMS disconnect. The backend refuses the DMS you logged in through (last_login_at set),
// which the UI already hides — this is the network path for the other connections.
async function disconnectDms({ dmsId, regionHost }: { dmsId: number; regionHost?: string }) {
  const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
  return await http.post(`${host}/api/oauth/disconnect_dms`, {
    dms_id: dmsId,
  })
}

// Careful: no connection for this DMS is a 400 (`no_connection`), not `has_access: false`.
// Until the backend's multi_dms_read flag is on, that's every DMS except the user's current one.
async function checkDmsAccess({ dmsId, regionHost }: { dmsId: number; regionHost?: string }) {
  const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
  const response = await http.post(`${host}/api/oauth/check_dms_access`, {
    dms_id: dmsId,
  })
  return parseWithZod(OAuthAccessSchema, response)
}

export const oauth = {
  checkClientAccess,
  getOauthAccess,
  generateOAuthUrl,
  generateDMSOAuthUrl,
  exchangeOAuthCode,
  exchangeDMSCode,
  checkOAuthAccess,
  disconnectOAuthProvider,
  disconnectDms,
  checkDmsAccess,
}
